import { useMcpTool } from "../lib/useMcpTool";
import McpCard from "../components/McpCard";
import DataTable, { Column } from "../components/DataTable";
import { PanelScroll } from "./ClientsPanel";

interface FinBrief {
  portfolio: { positions: number; total_value: number; unrealized_pnl: number };
  active_strategies: number; trades_today: number; pending_alerts: number;
  income: { active_streams: number };
  business_revenue: { lifetime: string; last_30d: string; payment_count: number };
}
interface Position { symbol?: string; asset_type?: string; quantity?: number; current_value?: number; unrealized_pnl?: number; [k: string]: unknown }

export default function FinancePanel() {
  const brief = useMcpTool<FinBrief>("financial", "financial_daily_brief");
  const portfolio = useMcpTool<{ rows: Position[] }>("financial", "query_portfolio", { limit: 50 });
  const positions = portfolio.data?.rows ?? [];
  const cols: Column<Position>[] = [
    { header: "Symbol", render: (p) => <span className="font-mono text-text font-medium">{p.symbol ?? "—"}</span> },
    { header: "Type", render: (p) => <span className="text-text-muted">{p.asset_type ?? "—"}</span> },
    { header: "Qty", render: (p) => <span className="font-mono">{p.quantity ?? "—"}</span>, className: "text-right" },
    { header: "Value", render: (p) => <span className="font-mono">{num(p.current_value)}</span>, className: "text-right" },
    { header: "Unreal. P&L", render: (p) => <Pnl v={p.unrealized_pnl} />, className: "text-right" },
  ];
  return (
    <PanelScroll accentVar="--biome-finance" title="Finance" subtitle="capital · revenue · trading">
      <McpCard title="Daily snapshot" loading={brief.loading} error={brief.error} onRefresh={brief.refresh} accentVar="--biome-finance">
        {brief.data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Revenue 30d" value={brief.data.business_revenue?.last_30d ?? "—"} />
            <Stat label="Lifetime" value={brief.data.business_revenue?.lifetime ?? "—"} />
            <Stat label="Payments" value={String(brief.data.business_revenue?.payment_count ?? "—")} />
            <Stat label="Income streams" value={String(brief.data.income?.active_streams ?? "—")} />
            <Stat label="Open positions" value={String(brief.data.portfolio?.positions ?? 0)} />
            <Stat label="Portfolio" value={num(brief.data.portfolio?.total_value)} />
            <Stat label="Trades today" value={String(brief.data.trades_today ?? 0)} />
            <Stat label="Alerts" value={String(brief.data.pending_alerts ?? 0)} warn={(brief.data.pending_alerts ?? 0) > 0} />
          </div>
        )}
      </McpCard>
      <McpCard title={`Portfolio${positions.length ? ` (${positions.length})` : ""}`} loading={portfolio.loading} error={portfolio.error} onRefresh={portfolio.refresh} accentVar="--biome-finance">
        <DataTable columns={cols} rows={positions} empty="No open positions — the RH engine reports flat." />
      </McpCard>
    </PanelScroll>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return <div><div className="text-caption text-text-dim uppercase tracking-wide">{label}</div><div className={`text-h2 font-mono mt-0.5 ${warn ? "text-warning" : "text-text"}`}>{value}</div></div>;
}
function Pnl({ v }: { v?: number }) {
  if (v == null) return <span className="text-text-dim">—</span>;
  const c = v > 0 ? "var(--success)" : v < 0 ? "var(--danger)" : "var(--text-muted)";
  return <span className="font-mono" style={{ color: c }}>{num(v)}</span>;
}
function num(v?: number) { return v == null ? "—" : v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }); }
